import React, { useState, useEffect, useRef } from 'react';
import PopUp from '../../general/popUps/PopUp';
import { createProviderClient, getOdooPublicTempUrl } from '../../../hooks/api/odoo';

function ProviderClient() {
  const [formData, setFormData] = useState({
    type: 'company', // 'individual' or 'company'
    name: '', // Nombre para individual
    companyName: '', // Nombre para compañía
    identificationNumber: '',
    identificationType: '15',
    phone: '',
    email: '',
    website: '',
    image: null
  });

  const [showPopUp, setShowPopUp] = useState(false);
  const [popUpData, setPopUpData] = useState({ message: "", action: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenExpiredMessage, setTokenExpiredMessage] = useState('');
  
  // Referencias para cleanup
  const isMountedRef = useRef(true);
  const fileReaderRef = useRef(null);
  
  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
    };
  }, []);

  // Verificar estado del token al cargar la página
  useEffect(() => {
    const checkTokenStatus = async () => {
      try {
        // Obtener entId de la URL
        const currentUrl = window.location.href;
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
    
        // Obtener el entId de la URL (ruta)
        const match = currentUrl.match(/provider-client\/([^/?]+)/);
        const entId = match ? match[1] : null;
        if (!entId) {
          return;
        }

        // Para el check de token status, no necesitamos paginación
        const response = await getOdooPublicTempUrl(entId, 1, 1, token);
        
        // Verificar si el componente sigue montado
        if (!isMountedRef.current) return;
        
        if (response && response.tokens && response.tokens.length > 0) {
          // Obtener el primer token del array
          const tokenData = response.tokens[0];
          
          // Si el token está vencido o no está disponible, mostrar mensaje
          if (tokenData.tokenExpired || !tokenData.available) {
            setTokenExpired(true);
            setTokenExpiredMessage(response.message || 'El link ha expirado. Por favor, solicita un nuevo link de registro.');
          } else {
            setTokenExpired(false);
            setTokenExpiredMessage('');
          }
        } else if (response && (!response.tokens || response.tokens.length === 0)) {
          // Si no hay tokens en la respuesta, considerar como expirado
          setTokenExpired(true);
          setTokenExpiredMessage(response.message || 'No se encontró un token válido. Por favor, solicita un nuevo link de registro.');
        }
      } catch (error) {
        console.error('Error al verificar estado del token:', error);
        // Si hay error, verificar si es porque el token está vencido
        if (error.response?.data) {
          const errorMessage = error.response.data.message || '';
          if (errorMessage.toLowerCase().includes('expirado') || 
              errorMessage.toLowerCase().includes('vencido') ||
              errorMessage.toLowerCase().includes('token is required')) {
            setTokenExpired(true);
            setTokenExpiredMessage(errorMessage || 'El link ha expirado. Por favor, solicita un nuevo link de registro.');
          }
        }
      }
    };

    checkTokenStatus();
  }, []);

  // Validar campos obligatorios
  const validateForm = () => {
    if (!formData.identificationNumber) {
      return { valid: false, message: 'El número de identificación es obligatorio.' };
    }
    if (formData.type === 'company' && !formData.companyName) {
      return { valid: false, message: 'El nombre de la compañía es obligatorio.' };
    }
    if (formData.type === 'individual' && !formData.name) {
      return { valid: false, message: 'El nombre es obligatorio.' };
    }
    // Validar formato de email si se proporciona
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        return { valid: false, message: 'El formato del correo electrónico no es válido.' };
      }
    }
    return { valid: true };
  };

  // Convertir imagen a base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      // Abortar FileReader anterior si existe
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
      
      const reader = new FileReader();
      fileReaderRef.current = reader;
      
      reader.onloadend = () => {
        if (!isMountedRef.current) {
          reject(new Error('Componente desmontado'));
          return;
        }
        const base64String = reader.result.split(',')[1] || reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => {
        if (isMountedRef.current) {
          reject(error);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Manejar cambios en los campos
  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Manejar imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      
      // Abortar FileReader anterior si existe
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
      
      // Crear preview de la imagen
      const reader = new FileReader();
      fileReaderRef.current = reader;
      
      reader.onloadend = () => {
        if (isMountedRef.current) {
          setImagePreview(reader.result);
        }
      };
      reader.onerror = () => {
        if (isMountedRef.current) {
          setShowPopUp(true);
          setPopUpData({
            message: 'Error al cargar la imagen. Por favor, intenta con otra imagen.',
            action: 'Error de imagen',
            status: 'error'
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Limpiar formulario
  const resetForm = () => {
    setFormData({
      type: 'company',
      name: '',
      companyName: '',
      identificationNumber: '',
      identificationType: '15',
      phone: '',
      email: '',
      website: '',
      image: null
    });
    setImagePreview(null);
    const imageUploadInput = document.getElementById('imageUpload');
    if (imageUploadInput) {
      imageUploadInput.value = '';
    }
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm();
    
    if (!validation.valid) {
      setShowPopUp(true);
      setPopUpData({
        message: validation.message,
        action: 'Campos incompletos',
        status: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      // Obtener entId y token de la URL
      const currentUrl = window.location.href;
      let entId = null;
      let token = null;
      
      // Intentar obtener entId del path
      const match = currentUrl.match(/provider-client\/([^/?]+)/);
      if (match) {
        entId = match[1];
      }
      
      // Obtener token y entId de query params
      const urlParams = new URLSearchParams(window.location.search);
      token = urlParams.get('token');
      if (!entId) {
        entId = urlParams.get('entId');
      }
      
      if (!entId) {
        setShowPopUp(true);
        setPopUpData({
          message: 'No se pudo obtener el ID de la empresa. Por favor, verifica la URL.',
          action: 'Error de configuración',
          status: 'error'
        });
        setLoading(false);
        return;
      }
      
      if (!token) {
        setShowPopUp(true);
        setPopUpData({
          message: 'No se pudo obtener el token. Por favor, verifica la URL.',
          action: 'Error de configuración',
          status: 'error'
        });
        setLoading(false);
        return;
      }

      // Convertir imagen a base64 si existe
      let photoBase64 = '';
      if (formData.image) {
        try {
          photoBase64 = await convertImageToBase64(formData.image);
        } catch (imageError) {
          console.error('Error al convertir imagen:', imageError);
          // Continuar sin la imagen si hay error
        }
      }

      // Preparar datos según el formato requerido
      const parsedIdentificationType = parseInt(formData.identificationType);
      const requestData = {
        name: formData.type === 'company' ? formData.companyName : formData.name,
        supplierType: formData.type === 'company' ? 'company' : 'person',
        identificationNumber: formData.identificationNumber.trim(),
        identificationTypeId: Number.isNaN(parsedIdentificationType) ? 15 : parsedIdentificationType,
        phone: formData.phone.trim() || '',
        email: formData.email.trim() || '',
        website: formData.website.trim() || '',
        photo: photoBase64 || '',
        countryId: 238 // Venezuela - ID hardcodeado
      };

      // Enviar datos a la API
      const response = await createProviderClient(entId, requestData, token);
      if (response) {
        setShowPopUp(true);
        setPopUpData({
          message: 'El formulario se ha enviado correctamente.',
          action: 'Formulario enviado',
          status: 'success'
        });
        // Limpiar formulario después de éxito (usar mismos valores que resetForm)
        setFormData({
          type: 'company',
          name: '',
          companyName: '',
          identificationNumber: '',
          identificationType: '15',
          phone: '',
          email: '',
          website: '',
          image: null
        });
        setImagePreview(null);
        const imageUploadInput = document.getElementById('imageUpload');
        if (imageUploadInput) {
          imageUploadInput.value = '';
        }
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      setShowPopUp(true);
      
      // Extraer el mensaje del error del backend
      let errorMessage = 'Error al enviar el formulario. Por favor, intenta nuevamente.';
      
      if (error.response?.data) {
        // Si el backend devuelve un objeto con message (caso: { statusCode: "10001", message: "..." })
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
          console.log('Mensaje extraído del error:', errorMessage);
        } 
        // Si el backend devuelve el mensaje directamente como string
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log('Mensaje que se mostrará en el popup:', errorMessage);
      setPopUpData({
        message: errorMessage,
        action: 'Error',
        status: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {showPopUp && (
        <PopUp 
          setShow={setShowPopUp} 
          message={popUpData.message} 
          action={popUpData.action} 
          status={popUpData.status} 
        />
      )}

      {/* Header */}
      <div className="bg-primary shadow-sm" style={{ background: 'linear-gradient(135deg, #014788 0%, #0056b3 100%)' }}>
        <div className="container py-3">
          <div className="d-flex align-items-center">
            <img 
              src='https://212global.com/wp-content/uploads/2020/08/Logos-de-212.png' 
              alt="212 Global" 
              style={{ height: '40px' }}
            />
            <div className="ml-3">
              <h4 className="text-white mb-0 fw-light" style={{ fontSize: '1.25rem' }}>Registro de Proveedor</h4>
              <small className="text-white-50" style={{ fontSize: '0.875rem' }}>Complete el formulario para registrarse</small>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-5">
        <h1 className='text-center mb-4' style={{ fontSize: '2rem', fontWeight: 'bold' }}>Registro de Proveedor</h1>
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            {tokenExpired ? (
              // Sección de Link Expirado
              <div 
                className="card border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                }}
              >
                <div className="card-body p-5 text-center">
                  <div className="mb-4">
                    <div 
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#dc3545',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto',
                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                      }}
                    >
                      <span 
                        style={{ 
                          fontSize: '4rem',
                          color: 'white',
                          fontWeight: 'bold',
                          lineHeight: 0,
                          marginTop: '-0.8rem'
                        }}
                      >
                        ×
                      </span>
                    </div>
                  </div>
                  
                  <h2 
                    className="mb-3"
                    style={{
                      color: '#991b1b',
                      fontWeight: 'bold',
                      fontSize: '1.3rem'
                    }}
                  >
                    Link Expirado
                  </h2>
                  
                  <p 
                    className="mb-4"
                    style={{
                      color: '#374151',
                      fontSize: '1.1rem',
                      lineHeight: '1.6',
                      maxWidth: '600px',
                      margin: '0 auto'
                    }}
                  >
                    {tokenExpiredMessage || 'El link de registro ha expirado. Por favor, solicita un nuevo link de registro para continuar.'}
                  </p>

                  <div 
                    className="p-4"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      maxWidth: '500px',
                      margin: '0 auto'
                    }}
                  >
                    <p 
                      className="mb-0"
                      style={{
                        color: '#6b7280',
                        fontSize: '0.95rem'
                      }}
                    >
                      <i className="fas fa-info-circle me-2" style={{ color: '#dc3545' }}></i>
                      Contacta con el administrador para obtener un nuevo link de registro.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm border-0">
                <div className="card-body p-5">
                  <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Columna Izquierda */}
                    <div className="col-md-6">
                      {/* Tipo de Registro */}
                      <div className="mb-3">
                        <label className="form-label fw-bold mb-3" style={{ fontSize: '1rem', color: '#333' }}>
                          Tipo de Registro
                        </label>
                        <div className="d-flex" style={{ gap: '2rem' }}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="type"
                              id="individual"
                              value="individual"
                              checked={formData.type === 'individual'}
                              onChange={(e) => handleChange('type', e.target.value)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label className="form-check-label" htmlFor="individual" style={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                              Individual
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="type"
                              id="company"
                              value="company"
                              checked={formData.type === 'company'}
                              onChange={(e) => handleChange('type', e.target.value)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label className="form-check-label" htmlFor="company" style={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                              Compañía
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Nombre (solo si es individual) */}
                      {formData.type === 'individual' && (
                        <div className="mb-3">
                          <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                            Nombre <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="Ingrese su nombre completo"
                            style={{ 
                              borderLeft: '3px solid #dc3545',
                              padding: '0.75rem',
                              fontSize: '0.95rem',
                              height: '45.33px'
                            }}
                            required
                          />
                        </div>
                      )}

                      {/* Correo electrónico */}
                      <div className="mb-3">
                        <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                          Correo electrónico
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="correo@ejemplo.com"
                          style={{ 
                            borderLeft: '3px solid #dc3545',
                            padding: '0.75rem',
                            fontSize: '0.95rem',
                            height: '45.33px'
                          }}
                        />
                      </div>

                      {/* Teléfono */}
                      <div className="mb-3">
                        <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); // Solo números
                            if (value.length <= 11) {
                              handleChange('phone', value);
                            }
                          }}
                          placeholder="Ej: 04121234567"
                          maxLength={11}
                          style={{ 
                            borderLeft: '3px solid #dc3545',
                            padding: '0.75rem',
                            fontSize: '0.95rem',
                            height: '45.33px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Columna Derecha */}
                    <div className="col-md-6">
                      {/* Imagen/Logo */}
                      <div className="mb-4">
                        <label className="form-label fw-bold d-block mb-3 text-right" style={{ fontSize: '1rem', color: '#333' }}>
                          Logo/Imagen
                        </label>
                        <div className="d-flex flex-column align-items-start">
                          {imagePreview ? (
                            <div 
                              className="mb-3 position-relative" 
                              style={{  
                                width: '100%', 
                                maxWidth: '220px',
                                marginLeft: 'auto',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                border: '2px solid #e9ecef',
                                backgroundColor: '#ffffff',
                                padding: '12px'
                              }}
                            >
                              <div
                                style={{
                                  width: '100%',
                                  height: '200px',
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  backgroundColor: '#f8f9fa',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #e9ecef'
                                }}
                              >
                                <img
                                  src={imagePreview}
                                  alt="Preview"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '12px'
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                style={{ 
                                  top: '20px', 
                                  right: '20px',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
                                  border: '2px solid #ffffff',
                                  zIndex: 10,
                                  backgroundColor: '#dc3545',
                                  color: '#ffffff',
                                  fontSize: '1.2rem',
                                  fontWeight: 'bold',
                                  lineHeight: '1'
                                }}
                                onClick={() => {
                                  setImagePreview(null);
                                  setFormData(prev => ({ ...prev, image: null }));
                                  document.getElementById('imageUpload').value = '';
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="mb-3"
                              style={{
                                width: '100%',
                                maxWidth: '220px',
                                marginLeft: 'auto',
                                height: '200px',
                                borderRadius: '16px',
                                border: '2px dashed #dee2e6',
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <div className="text-center text-muted">
                                <i className="fas fa-image" style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.5 }}></i>
                                <p className="small mb-0" style={{ fontSize: '0.875rem' }}>Vista previa</p>
                              </div>
                            </div>
                          )}
                          <div className="position-relative w-100 d-flex justify-content-end">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="d-none"
                              id="imageUpload"
                            />
                            <label
                              htmlFor="imageUpload"
                              className="btn"
                              style={{ 
                                borderLeft: '3px solid #ffc107',
                                cursor: 'pointer',
                                minWidth: '140px',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #dee2e6',
                                color: '#495057',
                                fontWeight: '500',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffffff';
                                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                              }}
                            >
                              <i className="fas fa-camera-plus me-2"></i>
                              {formData.image ? 'Cambiar' : 'Agregar'}
                            </label>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                        
                    {/* Número de Identificación */}
                      <div className="mb-4">
                        <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                          Número de Identificación <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex" style={{maxWidth: '550px'}}>
                          <select
                            className="form-select"
                            value={formData.identificationType}
                            onChange={(e) => handleChange('identificationType', e.target.value)}
                            style={{ 
                            borderLeft: '3px solid #dc3545',
                              maxWidth: '200px', 
                              padding: '0.6rem',
                              fontSize: '0.95rem'
                            }}
                          >
                            <option value="1">IVA</option>
                            <option value="2">Pasaporte</option>
                            <option value="3">Cédula Extranjera</option>
                            <option value="4">NIT</option>
                            <option value="5">Cédula de ciudadanía</option>
                            <option value="6">Registro Civil</option>
                            <option value="7">Tarjeta de Identidad</option>
                            <option value="8">Tarjeta de extranjería</option>
                            <option value="9">Cédula de extranjería</option>
                            <option value="10">PEP (Permiso Especial de Permanencia)</option>
                            <option value="11">Nit de otro país</option>
                            <option value="12">NIUP</option>
                            <option value="15">RIF</option>
                            <option value="16">Cédula de Identidad</option>
                            <option value="17">PPT (Permiso Protección Temporal)</option>
                          </select>
                          <input
                            type="text"
                            className="form-control h-100"
                            value={formData.identificationNumber}
                            onChange={(e) => handleChange('identificationNumber', e.target.value)}
                            placeholder="Ingrese el número de identificación"
                            style={{ 
                              padding: '0.75rem',
                              fontSize: '0.95rem'
                            }}
                            required
                            maxLength={13}
                          />
                        </div>
                      </div>

                      {/* Nombre de la Compañía (solo si es compañía) */}
                      {formData.type === 'company' && (
                        <div className="mb-4">
                          <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                            Nombre de la Compañía <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.companyName}
                            onChange={(e) => handleChange('companyName', e.target.value)}
                            placeholder="Ingrese el nombre de la compañía"
                            style={{ 
                              borderLeft: '3px solid #dc3545',
                              padding: '0.75rem',
                              fontSize: '0.95rem',
                              height: '45.33px'
                            }}
                            required
                          />
                        </div>
                      )}


                      {/* Sitio web */}
                      <div className="mb-4">
                        <label className="form-label fw-bold mb-2" style={{ fontSize: '0.95rem', color: '#333' }}>
                          Sitio web
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={formData.website}
                          onChange={(e) => handleChange('website', e.target.value)}
                          placeholder="Ej: https://www.ejemplo.com"
                          style={{ 
                            borderLeft: '3px solid #ffc107',
                            padding: '0.75rem',
                            fontSize: '0.95rem',
                            height: '45.33px'
                          }}
                        />
                      </div>

                    </div>
                  </div>

                  
                  {/* Botones de Acción */}
                  <div className="d-flex justify-content-end mt-5 pt-4 border-top" style={{ gap: '1.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4 py-2"
                      onClick={resetForm}
                      style={{ 
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1.5rem'
                      }}
                    >
                      Reiniciar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4 py-2"
                      disabled={loading || tokenExpired}
                      style={{
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #014788 0%, #0056b3 100%)',
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(1, 71, 136, 0.2)',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1.5rem'
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2"></i>
                          Enviar
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-light border-top mt-5">
        <div className="container py-3">
          <div className="text-center">
            <small className="text-muted" style={{ fontSize: '0.875rem' }}>
              © 2024 212 Global. Todos los derechos reservados. | 
              <span className="ml-2">Formulario de registro de proveedores</span>
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProviderClient;

